import React, { useState, useRef } from 'react';
import { Search, Download, FileText, X, Clock, ChevronDown, ChevronUp, Calendar, BookOpen, AlertCircle } from 'lucide-react';
import { api } from '../services/api';

const ConsultationVolumeHoraire = ({ teachers, activeYear, onClose }) => {
  // Fonction pour formater les heures décimales en "Xh Ymin"
  const formatDuration = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    
    if (h === 0) {
      return `${m} min`;
    } else if (m === 0) {
      return `${h} h`;
    } else {
      return `${h} h ${m} min`;
    }
  };

  // Fonction pour obtenir le nom du jour
  const getJourName = (jourNum) => {
    const jours = {
      1: 'Lundi',
      2: 'Mardi',
      3: 'Mercredi',
      4: 'Jeudi',
      5: 'Vendredi',
      6: 'Samedi',
      7: 'Dimanche'
    };
    return jours[jourNum] || `Jour ${jourNum}`;
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [volumeData, setVolumeData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Un echec de chargement n'affichait RIEN : l'appel ne leve jamais, il
  // rend { success: false }, donc le `catch` ne voyait jamais rien.
  const [erreur, setErreur] = useState(null);
  // Le jeton de la derniere demande. Sans lui, choisir A puis B et recevoir
  // la reponse de A en dernier affiche les heures de A sous le nom de B.
  const demandeRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedClasses, setExpandedClasses] = useState(new Set());

  const filteredTeachers = teachers.filter(teacher => 
    `${teacher.nom} ${teacher.prenom}`.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 10);

  const toggleClassExpansion = (classeId) => {
    setExpandedClasses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(classeId)) {
        newSet.delete(classeId);
      } else {
        newSet.add(classeId);
      }
      return newSet;
    });
  };

  const handleTeacherSelect = async (teacher) => {
    const demande = demandeRef.current + 1;
    demandeRef.current = demande;

    setSelectedTeacher(teacher);
    setSearchTerm('');
    setIsOpen(false);
    setLoading(true);
    setErreur(null);
    // Les heures du PRECEDENT partent tout de suite : un chargement rate les
    // laissait affichees sous le nouveau nom, et les exports ecrivaient le
    // nouveau nom sur les anciens chiffres.
    setVolumeData(null);
    setExpandedClasses(new Set());

    try {
      const response = await api.timetable.getVolumeHoraireDetaille(teacher.id, activeYear.id);

      // Une reponse qui n'est plus celle attendue ne doit rien ecrire.
      if (demandeRef.current !== demande) return;

      if (response.success) {
        setVolumeData(response.data);
      } else {
        setErreur(response?.message || "Les volumes horaires n'ont pas pu être lus.");
      }
    } catch (error) {
      if (demandeRef.current !== demande) return;
      console.error('Erreur récupération volume horaire:', error);
      setErreur("Les volumes horaires n'ont pas pu être lus.");
    } finally {
      if (demandeRef.current === demande) setLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedTeacher(null);
    setVolumeData(null);
    setSearchTerm('');
    setExpandedClasses(new Set());
  };

  const exportToPDF = async () => {
    const { default: JsPDF } = await import('jspdf');
    await import('jspdf-autotable');

    const doc = new JsPDF();
    
    // Titre
    doc.setFontSize(16);
    doc.text(`Volume Horaire - ${selectedTeacher.prenom} ${selectedTeacher.nom}`, 14, 15);
    doc.setFontSize(10);
    doc.text(`Année scolaire: ${activeYear.nom}`, 14, 22);

    let startY = 30;

    // Pour chaque classe
    volumeData.volumes.forEach((volume) => {
      // Titre de la classe
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`${volume.classe_nom} - ${formatDuration(volume.volume_horaire)}`, 14, startY);
      startY += 7;

      // Tableau des créneaux
      const creneauxData = volume.creneaux.map(c => [
        getJourName(c.jour_semaine),
        c.matiere_nom || '',
        `${c.heure_debut} - ${c.heure_fin}`,
        formatDuration(c.duree)
      ]);

      doc.autoTable({
        head: [['Jour', 'Matière', 'Horaire', 'Durée']],
        body: creneauxData,
        startY: startY,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [49, 145, 144] },
        margin: { left: 20 }
      });

      startY = doc.lastAutoTable.finalY + 10;

      // Nouvelle page si nécessaire
      if (startY > 250) {
        doc.addPage();
        startY = 20;
      }
    });

    // Page finale avec total
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(`TOTAL : ${formatDuration(volumeData.volume_total)}`, 14, startY + 10);

    doc.save(`volume-horaire-${selectedTeacher.nom}-${selectedTeacher.prenom}.pdf`);
  };

  const exportToExcel = async () => {
    const XLSX = await import('xlsx');
    
    const data = [];

    volumeData.volumes.forEach(volume => {
      // Titre de la classe
      data.push({
        'Classe': volume.classe_nom,
        'Jour': '',
        'Matière': '',
        'Horaire': '',
        'Durée': formatDuration(volume.volume_horaire)
      });

      // Créneaux
      volume.creneaux.forEach(c => {
        data.push({
          'Classe': '',
          'Jour': getJourName(c.jour_semaine),
          'Matière': c.matiere_nom || '',
          'Horaire': `${c.heure_debut} - ${c.heure_fin}`,
          'Durée': formatDuration(c.duree)
        });
      });

      // Ligne vide
      data.push({
        'Classe': '',
        'Jour': '',
        'Matière': '',
        'Horaire': '',
        'Durée': ''
      });
    });

    // Total
    data.push({
      'Classe': 'TOTAL',
      'Jour': '',
      'Matière': '',
      'Horaire': '',
      'Durée': formatDuration(volumeData.volume_total)
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Volume horaire");
    
    XLSX.writeFile(wb, `volume-horaire-${selectedTeacher.nom}-${selectedTeacher.prenom}.xlsx`);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-[#319190]" />
          <h3 className="text-base font-semibold text-gray-900">
            Consultation des Volumes Horaires
          </h3>
        </div>
        
        {selectedTeacher && (
          <button
            onClick={handleClearSelection}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title="Réinitialiser"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Champ de recherche */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un enseignant..."
          value={selectedTeacher ? `${selectedTeacher.prenom} ${selectedTeacher.nom}` : searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            if (selectedTeacher) {
              // ⚠️ `loading` devait partir AUSSI. Sans cela : on tape pendant le
              // chargement, la reponse arrive, et l'ecran se retrouve avec des
              // donnees sans enseignant - le bloc de resultats et l'etat vide
              // s'affichaient ensemble, et un export lisait `null.prenom`.
              // Le jeton rend la reponse en vol sans effet.
              demandeRef.current += 1;
              setSelectedTeacher(null);
              setVolumeData(null);
              setErreur(null);
              setLoading(false);
            }
          }}
          onFocus={() => setIsOpen(true)}
          className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:border-[#319190] focus:ring-1 focus:ring-[#319190] outline-none transition-all"
        />
        
        {/* Dropdown des résultats */}
        {isOpen && !selectedTeacher && filteredTeachers.length > 0 && searchTerm && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filteredTeachers.map(teacher => (
              <div
                key={teacher.id}
                onClick={() => handleTeacherSelect(teacher)}
                className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 transition-colors"
              >
                <div className="font-medium text-gray-900">
                  {teacher.prenom} {teacher.nom}
                </div>
                {teacher.matiere && (
                  <div className="text-sm text-gray-500">{teacher.matiere}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Affichage des résultats */}
      {erreur && !loading && (
          <div role="alert" className="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <p className="font-semibold">Lecture impossible</p>
              <p className="mt-0.5">{erreur}</p>
              <button
                type="button"
                onClick={() => selectedTeacher && handleTeacherSelect(selectedTeacher)}
                className="mt-2 rounded-lg border border-red-300 px-3 py-1 text-xs font-semibold transition-colors hover:bg-red-100"
              >
                Réessayer
              </button>
            </div>
          </div>
        )}

        {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#319190]"></div>
        </div>
      )}

      {/* `selectedTeacher` fait partie de la condition : ces chiffres sont
          ceux de QUELQU'UN, et les exports le nomment. */}
      {selectedTeacher && volumeData && !loading && (
        <div>
          {/* Boutons d'export */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={exportToExcel}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#246990] text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              <Download className="h-3.5 w-3.5" />
              Export Excel
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-white transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(to right, #c66d59, #ae7467)' }}
            >
              <FileText className="h-3.5 w-3.5" />
              Export PDF
            </button>
          </div>

          {/* Accordéons par classe */}
          <div className="space-y-2">
            {volumeData.volumes.map((volume) => {
              const isExpanded = expandedClasses.has(volume.classe_id);
              
              return (
                <div 
                  key={volume.classe_id} 
                  className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* En-tête de la classe */}
                  <button
                    onClick={() => toggleClassExpansion(volume.classe_id)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-colors flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#319190] bg-opacity-10">
                        <BookOpen className="h-4 w-4 text-[#319190]" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-900 text-base">
                          {volume.classe_nom}
                        </h4>
                        <p className="text-xs text-gray-500">
                          {volume.nombre_creneaux} créneau{volume.nombre_creneaux > 1 ? 'x' : ''}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-xl font-bold text-[#319190]">
                          {formatDuration(volume.volume_horaire)}
                        </div>
                        <div className="text-xs text-gray-400 uppercase tracking-wide">hebdo</div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {/* Détail des créneaux */}
                  {isExpanded && (
                    <div className="bg-white p-4 border-t border-gray-100">
                      <div className="mb-2 flex items-center gap-2 text-xs text-gray-600">
                        <Calendar className="h-3.5 w-3.5" />
                        <span className="font-medium">Détail des créneaux hebdomadaires :</span>
                      </div>
                      
                      <div className="space-y-1.5">
                        {volume.creneaux.map((creneau, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#319190] bg-opacity-20">
                                <span className="text-xs font-semibold text-[#319190]">
                                  {index + 1}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900 text-sm">
                                  {getJourName(creneau.jour_semaine)}
                                </div>
                                {creneau.matiere_nom && (
                                  <div className="text-xs text-gray-500">
                                    {creneau.matiere_nom}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <div className="text-xs font-medium text-gray-700">
                                  {creneau.heure_debut} - {creneau.heure_fin}
                                </div>
                              </div>
                              <div className="text-right min-w-[60px]">
                                <div className="text-xs font-semibold text-[#319190]">
                                  {formatDuration(creneau.duree)}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Calcul du total pour cette classe */}
                      <div className="mt-3 p-2.5 bg-[#319190] bg-opacity-5 rounded-lg border border-[#319190] border-opacity-20">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-700">
                            Total pour cette classe :
                          </span>
                          <span className="text-sm font-bold text-[#319190]">
                            {formatDuration(volume.volume_horaire)}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-600">
                          Calcul : {volume.creneaux.map(c => formatDuration(c.duree)).join(' + ')} = {formatDuration(volume.volume_horaire)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Total général */}
          <div className="mt-4 px-4 py-3 bg-gray-50 rounded-lg border-2 border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total hebdomadaire</div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {volumeData.volumes.length} classe{volumeData.volumes.length > 1 ? 's' : ''} • {volumeData.volumes.reduce((sum, v) => sum + v.nombre_creneaux, 0)} créneaux
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#319190]">
                  {formatDuration(volumeData.volume_total)}
                </div>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="mt-3 p-3 bg-blue-50 rounded-lg text-xs text-gray-700 border border-blue-100">
            <div className="flex items-start gap-2">
              <span className="text-base">💡</span>
              <div>
                <p className="font-medium">Volume horaire hebdomadaire</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Le calcul est basé sur les créneaux uniques de la semaine. Cliquez sur une classe pour voir le détail des créneaux et leur calcul.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message si aucun enseignant sélectionné */}
      {!selectedTeacher && !volumeData && !loading && (
        <div className="text-center py-12 text-gray-500">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <Clock className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <p className="text-lg font-medium text-gray-700">Recherchez un enseignant</p>
          <p className="text-sm mt-1">pour consulter son volume horaire hebdomadaire</p>
        </div>
      )}

      {/* Message si aucun créneau trouvé */}
      {selectedTeacher && volumeData && volumeData.volumes.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg font-medium text-gray-700">Aucun créneau trouvé</p>
          <p className="text-sm mt-1">pour cet enseignant</p>
        </div>
      )}
    </div>
  );
};

export default ConsultationVolumeHoraire;